import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  AfterViewInit
} from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {ApplicationDTO} from '../../../../../models/application/ApplicationDTO';
import {ApplicationPersonDTO} from '../../../../../models/application/ApplicationPersonDTO';
import {detailsExpand} from '../../../../../../common';
import {DateUtil} from '../../../../../../common/util/DateUtil';
import {MatSort} from '@angular/material/sort';
import {TableControl} from '../../../../../../common/models/TableControl';
import {MatSlideToggleChange} from '@angular/material/slide-toggle';
import {ApplicationConditionDTO} from '../../../../../models/application/ApplicationConditionDTO';
import {ApplicationContactDTO} from '../../../../../models/application/ApplicationContactDTO';
import {NameValueCodeDTO} from '../../../../../../common/models/NameValueCodeDTO';
import {ApplicationSchemeHistoryDTO} from '../../../../../models/application/ApplicationSchemeHistoryDTO';
import {ApplicationEmploymentDTO} from '../../../../../models/application/ApplicationEmploymentDTO';
import {getStatusColor} from '../../../../../../common/util/Util';

enum WaitingPeriodType {
  General = 'generalWaitingPeriod',
  PEC = 'preExistingConditionWaitingPeriod'
}

enum OverrideWaitingPeriodType {
  General = 'overrideGeneralWaitingPeriod',
  PEC = 'overridePreExistingConditionWaitingPeriod'
}

interface EditableApplicationPersonDTO extends ApplicationPersonDTO {
  editState?: {
    [key in WaitingPeriodType]: {
      isEditing: boolean;
      tempValue: number;
    };
  };
}

enum TableColumns {
  DependentNumber = 'dependentNumber',
  IdentificationType = 'identificationType',
  IdentificationNumber = 'identificationNumber',
  FullName = 'fullName',
  AssociationCode = 'associationCode',
  RelationshipTypeCode = 'relationshipTypeCode',
  Gender = 'gender',
  DateOfBirth = 'dateOfBirth',
  isVerified = 'isVerified',
  isAlive = 'isAlive',
  GeneralWaitingPeriod = 'generalWaitingPeriod',
  PECWaitingPeriod = 'pecWaitingPeriod'
}

@Component({
  selector: 'app-application-persons',
  templateUrl: './application-persons.component.html',
  styleUrls: ['./application-persons.component.scss'],
  animations: [detailsExpand]
})
export class ApplicationPersonsComponent implements OnInit, OnChanges, AfterViewInit {
  readonly getStatusColor = getStatusColor;

  readonly dateFormat = DateUtil.dateFormat;
  readonly tableColumns = TableColumns;
  readonly waitingPeriodType = WaitingPeriodType;

  @Input() application: ApplicationDTO;
  @Input() hasOnboardingRole = false;
  @Input() isApplicationLocked: boolean;
  @Input() diagnosisGroups: NameValueCodeDTO[];
  @Output() personsUpdated = new EventEmitter<EditableApplicationPersonDTO[]>();
  @Output() dispatchFetchRequest: EventEmitter<boolean> = new EventEmitter<boolean>();

  persons: EditableApplicationPersonDTO[];
  displayedColumns: string[] = Object.values(TableColumns);
  dataSource: MatTableDataSource<EditableApplicationPersonDTO> = new MatTableDataSource();
  expandedElement: ApplicationPersonDTO | null;
  tableControl: TableControl;
  length = 100;
  pageSize = 5;
  pageNumber = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];

  @ViewChild(MatSort, {static: false}) sort: MatSort;
  @ViewChild(MatPaginator, {static: false}) paginator: MatPaginator;

  constructor() {
    this.tableControl = new TableControl(
      this.length,
      this.pageSize,
      this.pageNumber,
      this.pageSizeOptions,
      'dependentNumber',
      'DESCENDING',
      '',
      ''
    );
  }

  get isDataExist(): boolean {
    return !!(this.persons && this.persons.length);
  }

  ngOnInit(): void {
    this.setupSortingDataAccessor();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.application && changes.application.currentValue) {
      this.persons = this.application.membership?.persons.map(person => ({
        ...person,
        editState: {
          [WaitingPeriodType.General]: {isEditing: false, tempValue: person[WaitingPeriodType.General]},
          [WaitingPeriodType.PEC]: {isEditing: false, tempValue: person[WaitingPeriodType.PEC]}
        }
      }));
      this.dataSource = new MatTableDataSource(this.persons);
      this.setupSortingDataAccessor();

      // Re-apply sort and paginator after data changes
      if (this.sort) {
        this.dataSource.sort = this.sort;
      }
      if (this.paginator) {
        this.dataSource.paginator = this.paginator;
      }
    }
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  getFullName(person: ApplicationPersonDTO): string {
    return `${person.firstName} ${person.middleName || ''} ${person.lastName}`.trim();
  }

  getVerificationStatus(status: number): string {
    switch (status) {
    case -1:
      return 'No';
    case 0:
      return 'Unknown';
    case 1:
      return 'Yes';
    default:
      return 'Unknown';
    }
  }

  pageChanged($event: PageEvent): void {
    this.tableControl.pageNumber = $event.pageIndex;
    this.tableControl.pageSize = $event.pageSize;
  }

  toggleEdit($event: MouseEvent, person: EditableApplicationPersonDTO, field: WaitingPeriodType): void {
    $event.preventDefault();
    $event.stopPropagation();

    const editState = person.editState[field];
    if (editState.isEditing) {
      editState.tempValue = person[field];  // Canceling edit, revert to original value
    }
    editState.isEditing = !editState.isEditing;
    this.personsUpdated.emit(this.persons);
  }

  saveWaitingPeriod($event: MouseEvent, person: EditableApplicationPersonDTO, field: WaitingPeriodType): void {
    $event.preventDefault();
    $event.stopPropagation();

    const editState = person.editState[field];
    person[field] = editState.tempValue;
    person[field === WaitingPeriodType.General ? OverrideWaitingPeriodType.General : OverrideWaitingPeriodType.PEC] = true;
    editState.isEditing = false;
    this.personsUpdated.emit(this.persons);
  }

  onGeneralWaitingPeriodInclPMBToggleChange(event: MatSlideToggleChange, applicationPersonId: number): void {
    const checked: boolean = event?.checked;
    const person = this.persons.find(x => x.applicationPersonId === applicationPersonId);
    if (person) {
      person.generalWaitingPeriodIncPMBIndicator = checked;
    }
    this.personsUpdated.emit(this.persons);
  }

  onPecWaitingPeriodInclPMBToggleChange(event: MatSlideToggleChange, applicationPersonId: number): void {
    const checked: boolean = event?.checked;
    const person = this.persons.find(x => x.applicationPersonId === applicationPersonId);
    if (person) {
      person.pecWaitingPeriodIncPMBIndicator = checked;
    }
    this.personsUpdated.emit(this.persons);
  }

  handleConditionChange(conditionsEvent: { conditions: ApplicationConditionDTO[]; }, applicationPersonId: number): void {
    const selectedPerson = this.persons.find(x => x.applicationPersonId === applicationPersonId);

    if (selectedPerson) {
      if (!selectedPerson.conditions) {
        selectedPerson.conditions = [];
      }
      selectedPerson.conditions = conditionsEvent.conditions;
      this.persons = [...this.persons];
      this.personsUpdated.emit(this.persons);
      this.dispatchFetchRequest.emit(true);
    }
  }

  handleContactDetailsUpdate(contactDetailEvent: {
    applicationPersonId: number;
    contactDetails: ApplicationContactDTO[];
  }): void {
    const selectedPerson = this.persons.find(person =>
      person.applicationPersonId === contactDetailEvent.applicationPersonId
    );

    if (selectedPerson) {
      if (!selectedPerson.contacts) {
        selectedPerson.contacts = [];
      }
      selectedPerson.contacts = contactDetailEvent.contactDetails;
      this.persons = [...this.persons];
      this.personsUpdated.emit(this.persons);
      this.dispatchFetchRequest.emit(true);
    }
  }

  handleSchemeHistoryUpdate(schemeHistoryEvent: {
    applicationPersonId: number;
    schemeHistory: ApplicationSchemeHistoryDTO;
    isEdit: boolean;
    isDelete?: boolean;
  }): void {
    const selectedPerson = this.persons.find(person =>
      person.applicationPersonId === schemeHistoryEvent.applicationPersonId
    );

    if (selectedPerson) {
      if (!selectedPerson.schemeHistory) {
        selectedPerson.schemeHistory = [];
      }
      selectedPerson.schemeHistory = this.updateSchemeHistory(selectedPerson.schemeHistory, schemeHistoryEvent);
      this.persons = [...this.persons];
      this.personsUpdated.emit(this.persons);
      this.dispatchFetchRequest.emit(true);
    }
  }

  handleEmploymentUpdate(employmentEvent: {
    applicationPersonId: number;
    employment: ApplicationEmploymentDTO;
    isEdit: boolean;
    isDelete?: boolean;
  }): void {
    const selectedPerson = this.persons.find(person =>
      person.applicationPersonId === employmentEvent.applicationPersonId
    );

    if (selectedPerson) {
      if (!selectedPerson.employmentDetails) {
        selectedPerson.employmentDetails = [];
      }

      if (employmentEvent.isDelete) {
        selectedPerson.employmentDetails = selectedPerson.employmentDetails.filter(
          existing => existing.applicationEmploymentId !== employmentEvent.employment.applicationEmploymentId
        );
      } else if (employmentEvent.isEdit) {
        const index = selectedPerson.employmentDetails.findIndex(
          existing => existing.applicationEmploymentId === employmentEvent.employment.applicationEmploymentId
        );
        if (index > -1) {
          selectedPerson.employmentDetails[index] = employmentEvent.employment;
        }
      } else {
        selectedPerson.employmentDetails.push(employmentEvent.employment);
      }

      this.persons = [...this.persons];
      this.personsUpdated.emit(this.persons);
      this.dispatchFetchRequest.emit(true);
    }
  }

  private setupSortingDataAccessor(): void {
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
      case TableColumns.FullName:
        return this.getFullName(item);
      case TableColumns.IdentificationType:
        return item.identification?.idTypeCode;
      case TableColumns.IdentificationNumber:
        return item.identification?.idNumber;
      case TableColumns.isVerified:
        return item.identification?.isVerified;
      case TableColumns.isAlive:
        return item.identification?.isAlive;
      default:
        return item[property];
      }
    };
  }

  private updateSchemeHistory(
    existingSchemeHistoryList: ApplicationSchemeHistoryDTO[],
    event: {
      schemeHistory: ApplicationSchemeHistoryDTO;
      isEdit: boolean;
      isDelete?: boolean;
    }
  ): ApplicationSchemeHistoryDTO[] {
    const {schemeHistory, isEdit, isDelete} = event;

    if (isDelete) {
      return this.removeSchemeHistory(existingSchemeHistoryList, schemeHistory);
    }

    if (isEdit) {
      return this.updateExistingSchemeHistory(existingSchemeHistoryList, schemeHistory);
    }

    return this.addNewSchemeHistory(existingSchemeHistoryList, schemeHistory);
  }

  private removeSchemeHistory(
    schemeHistoryList: ApplicationSchemeHistoryDTO[],
    schemeToRemove: ApplicationSchemeHistoryDTO
  ): ApplicationSchemeHistoryDTO[] {
    if (!schemeHistoryList?.length) {
      return [];
    }

    return schemeHistoryList
      .filter((existingScheme: ApplicationSchemeHistoryDTO) => {
        // Compare by ID if both records have IDs
        if (existingScheme.applicationSchemeHistoryId && schemeToRemove.applicationSchemeHistoryId) {
          return existingScheme.applicationSchemeHistoryId !== schemeToRemove.applicationSchemeHistoryId;
        }
        // Compare key fields for newly added records
        return !this.isSameSchemeHistory(existingScheme, schemeToRemove);
      });
  }

  private isSameSchemeHistory(first: ApplicationSchemeHistoryDTO, second: ApplicationSchemeHistoryDTO): boolean {
    return (
      first.scheme?.schemeCode === second.scheme?.schemeCode &&
      first.scheme?.schemeName === second.scheme?.schemeName &&
      first.membershipNumber === second.membershipNumber
    );
  }

  private updateExistingSchemeHistory(
    schemeHistoryList: ApplicationSchemeHistoryDTO[],
    updatedScheme: ApplicationSchemeHistoryDTO
  ): ApplicationSchemeHistoryDTO[] {
    const index = schemeHistoryList?.findIndex(scheme =>
      scheme.applicationSchemeHistoryId === updatedScheme.applicationSchemeHistoryId
    );

    if (index === -1) {
      return schemeHistoryList;
    }

    const updatedList: ApplicationSchemeHistoryDTO[] = [...schemeHistoryList];
    updatedList[index] = updatedScheme;
    return updatedList;
  }

  private addNewSchemeHistory(
    schemeHistoryList: ApplicationSchemeHistoryDTO[],
    newScheme: ApplicationSchemeHistoryDTO
  ): ApplicationSchemeHistoryDTO[] {
    return [...(schemeHistoryList || []), newScheme];
  }
}
